import { Button, HookedForm, InputField, SelectField } from '@app/components';
import { PhoneField } from '@app/components/Fields/PhoneField';
import { useNavigate } from 'react-router-dom';
import Logo from '@app/assets/vertical-logo.svg?react';
import BackIcon from '@app/assets/icons/back.svg?react';
import { useNewBusinessPage } from './NewBusiness.hooks';
import { BusinessCategoryInterface, PlaceInterface } from '@app/models/business';
import { searchPlaces } from '@app/services/business';
import { useState, useEffect, useRef } from 'react';

export const NewBusiness = () => {
  const {
    handleSubmit,
    newBusinessForm,
    businessCategories,
    isLoading,
    businessEmailError,
    isValidatingBusinessEmail,
    clearBusinessEmailError,
  } = useNewBusinessPage();
  const navigate = useNavigate();
  const watchedEmail = newBusinessForm.watch('email');

  useEffect(() => {
    if (businessEmailError) clearBusinessEmailError();
  }, [watchedEmail]);


  // Business name search functionality
  const [places, setPlaces] = useState<PlaceInterface[]>([]);
  const [showPlacesDropdown, setShowPlacesDropdown] = useState(false);
  const [isSearchingPlaces, setIsSearchingPlaces] = useState(false);
  const [userLocation, setUserLocation] = useState<string>('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get user location on component mount and populate form with stored data
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation(`${latitude},${longitude}`);
        },
        (error) => {
          console.error('Error getting location:', error);
          // Fallback to a default location (San Francisco)
          setUserLocation('37.7749,-122.4194');
        }
      );
    } else {
      // Fallback to a default location if geolocation is not supported
      setUserLocation('37.7749,-122.4194');
    }

    // Check for stored business data and populate form
    const storedBusinessData = localStorage.getItem('pendingBusinessData');
    if (storedBusinessData) {
      try {
        const businessData = JSON.parse(storedBusinessData);
        newBusinessForm.setValue('name', businessData.name || '');
        newBusinessForm.setValue('email', businessData.email || '');
        newBusinessForm.setValue('phoneNumber', businessData.phoneNumber || '');
        newBusinessForm.setValue('address', businessData.address || '');
        newBusinessForm.setValue('category', businessData.category || '');

        // Trigger business name search if name exists
        if (businessData.name) {
          handleBusinessNameChange(businessData.name);
        }
      } catch (error) {
        console.error('Error parsing stored business data:', error);
      }
    }
  }, []);

  // Search places when business name changes
  const handleBusinessNameChange = (value: string) => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Only search if query is longer than 3 characters and we have location
    if (value.length > 3 && userLocation) {
      // Debounce the search
      searchTimeoutRef.current = setTimeout(async () => {
        setIsSearchingPlaces(true);
        try {
          const response = await searchPlaces(value, userLocation);
          setPlaces(response.data.places || []);
          setShowPlacesDropdown(true);
        } catch (error) {
          console.error('Error searching places:', error);
          setPlaces([]);
        } finally {
          setIsSearchingPlaces(false);
        }
      }, 500); // 500ms debounce
    } else {
      setPlaces([]);
      setShowPlacesDropdown(false);
    }
  };

  // Handle place selection
  const handlePlaceSelect = (place: PlaceInterface) => {
    newBusinessForm.setValue('name', place.name);
    newBusinessForm.setValue('address', place.address);
    setShowPlacesDropdown(false);
    setPlaces([]);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="flex items-center justify-center flex-col min-h-screen py-6">
      <Logo width={139} height={110} className="mb-8" />
      <div className="bg-white text-center space-y-6 rounded-lg max-w-[1200px] px-20 pt-12 pb-8 shadow-sm">
        <div className="flex items-center text-left w-full">
          <Button
            kind="tertiary"
            className="min-w-fit !w-fit text-[#6B7280]"
            leftNode={<BackIcon />}
            onClick={() => navigate('/select-business')}
          >
            Back to Businesses
          </Button>
        </div>
        <div className="space-y-2">
          <h2 className="font-bold text-2xl text-[#1F2937]">
            New business account
          </h2>
          <p className="text-sm text-[#6B7280]">
            Just a few details needed to set up your account
          </p>
        </div>

        <HookedForm
          formHook={newBusinessForm}
          onSubmit={handleSubmit}
          className="space-y-4"
        >
          <div className="space-y-2 text-left relative">
            <label className="block text-sm font-medium text-[#374151]">
              Business name
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="e.g. Luskin's"
                className="bg-base-seasalt rounded py-3 px-4 pr-12 w-full outline-brand-baseBlue outline-1"
                value={newBusinessForm.watch('name') || ''}
                onChange={(e) => {
                  newBusinessForm.setValue('name', e.target.value);
                  handleBusinessNameChange(e.target.value);
                }}
              />
              {isSearchingPlaces && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#52B6FE]"></div>
                </div>
              )}
            </div>

            {/* Places dropdown */}
            {showPlacesDropdown && places.length > 0 && (
              <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
                <div className="overflow-y-auto max-h-60">
                  {places.map((place, index) => (
                    <div
                      key={index}
                      className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      onClick={() => handlePlaceSelect(place)}
                    >
                      <div className="font-medium text-gray-900 truncate">
                        {place.name}
                      </div>
                      <div className="text-sm text-gray-600 truncate">
                        {place.address}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2 text-left">
            <label className="block text-sm font-medium text-[#374151]">
              Business email
            </label>
            <InputField
              name="email"
              type="email"
              placeholder="e.g. contact@yourbusiness.com"
              className="w-full text-sm"
            />
            {businessEmailError && (
              <p className="text-red-500 text-sm mt-1">{businessEmailError}</p>
            )}
          </div>

          <div className="space-y-2 text-left">
            <PhoneField name="phoneNumber" label="Business phone" isOptional />
          </div>

          <div className="space-y-2 text-left">
            <label className="block text-sm font-medium text-[#374151]">
              Business address
            </label>
            <InputField
              name="address"
              type="text"
              placeholder="e.g. 1341 Poplar Street, Chicago, IL 60606"
              className="w-full text-sm"
            />
          </div>

          <div className="space-y-2 text-left">
            <label className="block text-sm font-medium text-[#374151]">
              Company type
            </label>
            <SelectField
              name="category"
              placeholder="Select Company type"
              options={businessCategories?.categories?.map(
                (category: BusinessCategoryInterface) => ({
                  value: category.id,
                  label: category.name,
                })
              )}
              className="w-full text-sm"
            />
          </div>

          <div className="flex items-center space-x-2 text-left">
            <input
              type="checkbox"
              id="terms"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="w-4 h-4 border border-[#D1D5DB] rounded focus:ring-2 focus:ring-[#52B6FE] checked:bg-[#52B6FE] checked:border-[#52B6FE]"
            />
            <label htmlFor="terms" className="text-sm text-[#374151]">
              I agree to{' '}
              <a
                href="/terms-of-service"
                className="text-[#52B6FE] underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href="/privacy-policy"
                className="text-[#52B6FE] underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              .
            </label>
          </div>

          <Button
            disabled={isLoading || !termsAccepted || isValidatingBusinessEmail}
            loading={isValidatingBusinessEmail}
            className="w-full"
          >
            Choose a Plan
          </Button>
        </HookedForm>
      </div>
    </div>
  );
};
